import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Dropdown, MenuProps, Space, Tooltip } from 'antd'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ExportOutlined,
  FileExcelOutlined,
  LoadingOutlined,
  PlusOutlined,
  UploadOutlined,
  FileSearchOutlined
} from '@ant-design/icons'
import { DatePicker } from '../../../components/Form'
import LoadingWrapper from '../../../components/Misc/LoadingWrapper'
import InvestmentGroup from '../../../features/investment/components/InvestmentGroup'
import InvestmentModal from '../../../features/investment/components/InvestmentModal'
import { setInvestmentPageDateAction } from '../../../redux/pages/investments/actions'
import { investmentsPageSelector } from '../../../redux/pages/investments/selectors'
import InvestmentSummary from '../../../features/investment/components/InvestmentSummary'
import { filtersSelector } from '../../../redux/context/filters/selectors'
import { GroupSettingDataSource } from '../../../features/companyGroup/types/CompanyGroupSettings'
import { notLocked } from '../../settings/company/budgeting/budgetingScenarios/utils'
import PageHeaderWrapper from '../../../components/PageHeaderWrapper/PageHeaderWrapper'
import DocumentationLink from '../../../components/Misc/DocumentationLink'
import { AppDispatch } from '../../../redux/store'
import { useInvestmentSummary } from '../../../features/investment/hooks/useInvestment'
import { useInvestmentExport } from '../../../features/investment/hooks/useInvestmentExport'
import { useInvestmentTemplate } from '../../../features/investment/hooks/useInvestmentTemplate'
import InvestmentImport from '../../../features/investment/components/InvestmentImport'
import { useCtxCompanyGroupBudgetSource } from '../../../features/companyGroup/queries/companyGroupSetting/useCtxCompanyGroupSettings'
import { useDepreciationVoucherPreview } from '../../../features/investment/hooks/useDepreciationVoucherPreview'
import DepreciationVoucherModal from '../../../features/investment/components/DepreciationVoucherModal'
import { useAuthority } from '../../../utils/Authorizable/authorize'

interface InvestmentPageProps {
  isExisting: boolean
}

const InvestmentsPage: React.FC<InvestmentPageProps> = ({ isExisting }) => {
  const dispatch: AppDispatch = useDispatch()

  const location = useLocation()
  const pagePath = location.pathname
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    data: investmentsData,
    isLoading: investmentsLoading,
    isFetching,
    isRefetching
  } = useInvestmentSummary(isExisting)
  const [modalVisible, setModalVisible] = useState<boolean>(false)
  const { budgetingScenario } = useSelector(filtersSelector)
  const { data: groupBudgetSourceSetting } = useCtxCompanyGroupBudgetSource()
  const { download: downloadReport, isLoading: isDownLoadingReport } = useInvestmentExport(isExisting)
  const { download: downloadTemplate, isLoading: isDownLoadingTemplate } = useInvestmentTemplate()
  const { date } = useSelector(investmentsPageSelector)
  const {
    fetchPreview,
    submitVoucher,
    isLoading: isLoadingPreview,
    isSubmitting,
    voucherData,
    clearData
  } = useDepreciationVoucherPreview()
  const [voucherModalVisible, setVoucherModalVisible] = useState<boolean>(false)

  // Authority checks for depreciation voucher creation
  const hasDepreciationVoucherAccess =
    useAuthority({
      permission: 'write-depreciation-vouchers'
    }) && !budgetingScenario?.id

  const tabs = [
    {
      key: '/investments/existing',
      label: t('menu:/investments/existing')
    },
    {
      key: '/investments/investment',
      label: t('menu:/investments/investment')
    }
  ]

  const toggleModal = () => {
    setModalVisible(!modalVisible)
  }

  const menuButtonItemsInvestments: MenuProps['items'] = [
    {
      key: 'download-investments',
      label: isExisting ? t('investmentsPage:downloadFixedAssets') : t('investmentsPage:downloadInvestments'),
      icon: <ExportOutlined />
    }
  ]

  const isLocked = !notLocked(budgetingScenario) || groupBudgetSourceSetting === GroupSettingDataSource.subsidiaries

  const menuButtonItemsExisting: MenuProps['items'] = [
    {
      key: 'import',
      label: <InvestmentImport />,
      icon: <UploadOutlined />,
      disabled: isLocked
    },
    {
      key: 'download-csv',
      label: t('investmentsPage:csvDownload'),
      icon: <FileExcelOutlined />
    },
    ...menuButtonItemsInvestments,
    ...(hasDepreciationVoucherAccess
      ? [
          {
            key: 'create-depreciation-voucher',
            label: t('investmentsPage:createDepreciationVoucher'),
            icon: isLoadingPreview ? <LoadingOutlined /> : <FileSearchOutlined />
          }
        ]
      : [])
  ]

  const handleMenuClick = async ({ key }: any) => {
    switch (key) {
      case 'download-csv':
        downloadTemplate()
        break
      case 'download-investments':
        downloadReport()
        break
      case 'create-depreciation-voucher':
        if (date) {
          try {
            await fetchPreview(date)
            setVoucherModalVisible(true)
          } catch (error) {
            // Error is already handled in the hook
          }
        }
        break
      default:
        break
    }
  }

  const closeVoucherModal = () => {
    setVoucherModalVisible(false)
    clearData()
  }

  const handleVoucherSubmit = async (submitDate: string) => {
    await submitVoucher(submitDate)
  }

  return (
    <>
      <InvestmentModal
        onlyDepreciable
        sourceDisabled={false}
        formType="investment"
        isExisting={isExisting}
        visible={modalVisible}
        onFinish={toggleModal}
      />
      <DepreciationVoucherModal
        visible={voucherModalVisible}
        onClose={closeVoucherModal}
        onSubmit={handleVoucherSubmit}
        voucherData={voucherData}
        voucherDate={date}
        loading={isLoadingPreview}
        submitting={isSubmitting}
      />
      <PageHeaderWrapper
        title={t('investmentsPage:investment')}
        subTitle={
          <Space>
            {t(`menu:${pagePath}`)}
            <Tooltip placement="right" title="info">
              <DocumentationLink route={{ path: '/budgeting' }} />
            </Tooltip>
          </Space>
        }
        onTabChange={(key: string) => {
          navigate(key)
        }}
        extra={[
          <DatePicker
            value={date}
            format="MM/YYYY"
            picker="month"
            key="month"
            onChange={val => dispatch(setInvestmentPageDateAction(val))}
          />,
          <Dropdown.Button
            icon={
              isDownLoadingReport || isDownLoadingTemplate || isLoadingPreview || isSubmitting ? (
                <LoadingOutlined />
              ) : undefined
            }
            onClick={toggleModal}
            key="dropdown"
            type="primary"
            buttonsRender={([leftButton, rightButton]) => [
              React.cloneElement(leftButton as React.ReactElement, { disabled: isLocked }),
              rightButton
            ]}
            menu={{
              items: isExisting ? menuButtonItemsExisting : menuButtonItemsInvestments,
              onClick: handleMenuClick
            }}
          >
            <PlusOutlined />
            {t('global:add-new')}
          </Dropdown.Button>
        ]}
        content={<InvestmentSummary summary={investmentsData?.summary} />}
        tabActiveKey={pagePath}
        tabList={tabs}
        ghost={false}
      >
        <LoadingWrapper loading={date ? investmentsLoading || isFetching || isRefetching : false}>
          <Space style={{ width: '100%' }} direction="vertical" size="large">
            {investmentsData?.balanceSheetRows.map(balanceSheetRow => (
              <InvestmentGroup key={balanceSheetRow.balanceSheetRowId} {...balanceSheetRow} isExisting={isExisting} />
            ))}
          </Space>
        </LoadingWrapper>
      </PageHeaderWrapper>
    </>
  )
}

export default InvestmentsPage
